import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Lock } from "lucide-react";
import { useState } from "react";

interface UnlockLogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logId: number | null;
  onUnlock: (logId: number, editReason: string) => void;
  isLoading?: boolean;
}

export function UnlockLogModal({
  open,
  onOpenChange,
  logId,
  onUnlock,
  isLoading = false,
}: UnlockLogModalProps) {
  const [editReason, setEditReason] = useState("");

  const handleUnlock = () => {
    if (!logId || !editReason.trim()) {
      return;
    }
    onUnlock(logId, editReason);
    setEditReason("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-amber-500" />
            <DialogTitle>Unlock Log for Editing</DialogTitle>
          </div>
          <DialogDescription>
            This log is currently locked. Please provide a reason for unlocking and editing this log.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="editReason">
              Edit Reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="editReason"
              placeholder="e.g., Correcting meter reading error, Adding missing chemical data..."
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              rows={4}
              required
            />
            <p className="text-sm text-muted-foreground">
              This reason will be recorded in the audit trail.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              onOpenChange(false);
              setEditReason("");
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUnlock}
            disabled={!editReason.trim() || isLoading}
          >
            {isLoading ? "Unlocking..." : "Unlock and Edit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
